/**
 * Route guard for everything under src/app/platform/* — the Bero staff
 * console. Pattern-matches features/account-admin/AdminGate.tsx: a resident
 * session user must be PLATFORM_ADMIN (cheap client-side check first), then
 * GET /api/platform/me confirms an active platform_members row and hands
 * back the real platform role (SUPER_ADMIN/SALES/SUPPORT/MARKETING) — the
 * backend's @require_platform is the real guard either way, this just keeps
 * a non-member PLATFORM_ADMIN account (shouldn't exist, but fail closed) or
 * a resident who guesses the URL from ever seeing the console shell.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';

import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { EmptyState, Screen, Skeleton, TopBar } from '@/ui';

const PlatformRoleContext = createContext<string | null>(null);

/** Must be called from inside a mounted PlatformGate (i.e. anywhere under
 * src/app/platform/*) — throws otherwise so a misplaced usage fails loud. */
export function usePlatformRole(): string {
  const role = useContext(PlatformRoleContext);
  if (!role) throw new Error('usePlatformRole used outside PlatformGate');
  return role;
}

type GateState = 'checking' | 'denied' | 'ok';

export function PlatformGate({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const { t } = useT();
  const { user, loading: sessionLoading } = useSession();
  const [state, setState] = useState<GateState>('checking');
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    if (sessionLoading) return;
    if (user?.role !== 'PLATFORM_ADMIN') {
      void Promise.resolve().then(() => setState('denied'));
      return;
    }
    let cancelled = false;
    void Promise.resolve().then(() => { if (!cancelled) setState('checking'); });
    api.get<{ role: string }>('/api/platform/me')
      .then((body) => {
        if (cancelled) return;
        setRole(body.role);
        setState('ok');
      })
      .catch(() => {
        if (!cancelled) setState('denied');
      });
    return () => { cancelled = true; };
  }, [sessionLoading, user?.role]);

  if (sessionLoading || state === 'checking') {
    return (
      <Screen scroll={false} padded>
        <Skeleton h={22} w="30%" style={{ marginTop: 24, marginBottom: 20 }} />
        <Skeleton h={72} style={{ marginBottom: 12 }} />
        <Skeleton h={72} style={{ marginBottom: 12 }} />
        <Skeleton h={72} />
      </Screen>
    );
  }

  if (state === 'denied' || !role) {
    return (
      <Screen scroll={false}>
        <TopBar title={t('plat_title')} brand="none" />
        <EmptyState
          icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
          title={t('plat_gate_denied_title')}
          body={t('plat_gate_denied_body')}
        />
      </Screen>
    );
  }

  return <PlatformRoleContext.Provider value={role}>{children}</PlatformRoleContext.Provider>;
}
